/**
 * Service that monitors user authentication state across tabs
 * Detects when the auth cookie has been cleared (indicating logout in another tab)
 */

import { hasLostAuthentication } from '../utils/auth-cookie-checker';

let checkAuthInterval = null;
let lastCheckTime = 0;
let isCheckingAuth = false;
let storeSubscription = null;
let previousAuthState = null;
const CHECK_DEBOUNCE_MS = 1000; // Don't check more than once per second
const PERIODIC_CHECK_INTERVAL_MS = 60000; // 1 minute - only check while authenticated

/**
 * Start the periodic check interval (called when user logs in)
 */
const startPeriodicCheck = (reduxStore, signOutUserAction, api) => {
  if (checkAuthInterval) {
    return;
  }
  checkAuthInterval = setInterval(() => {
    performAuthCheck(reduxStore, signOutUserAction, api, 'interval');
  }, PERIODIC_CHECK_INTERVAL_MS);
};

/**
 * Stop the periodic check interval (called when user logs out)
 */
const stopPeriodicCheck = () => {
  if (checkAuthInterval) {
    clearInterval(checkAuthInterval);
    checkAuthInterval = null;
  }
};

/**
 * Perform the actual auth check and logout if needed
 */
const performAuthCheck = (reduxStore, signOutUserAction, api) => {
  if (isCheckingAuth) {
    return;
  }

  isCheckingAuth = true;
  try {
    const fullState = reduxStore.getState();
    const authState = fullState.authentication;

    if (hasLostAuthentication(authState)) {
      stopPeriodicCheck();
      reduxStore.dispatch(signOutUserAction({ api, clearCache: true }));
    }
  } catch (e) {
    // Silently handle errors
  } finally {
    isCheckingAuth = false;
  }
};

/**
 * Start listening for authentication state changes
 * Triggers logout if cookie was cleared in another tab
 */
export const startAuthStateListener = (reduxStore, signOutUserAction, api) => {
  // Check auth state on user interactions (debounced)
  const checkAuthOnInteraction = () => {
    const now = Date.now();
    if (now - lastCheckTime < CHECK_DEBOUNCE_MS) {
      return;
    }
    lastCheckTime = now;
    performAuthCheck(reduxStore, signOutUserAction, api);
  };

  // Listen for user interactions
  const events = ['click'];
  events.forEach((event) => {
    document.addEventListener(event, checkAuthOnInteraction, { passive: true });
  });

  // Monitor authentication state changes to control periodic check
  storeSubscription = reduxStore.subscribe(() => {
    const state = reduxStore.getState();
    const currentAuthState = state.authentication?.authentication;

    // Only process if authentication state actually changed
    if (currentAuthState === previousAuthState) {
      return;
    }
    previousAuthState = currentAuthState;

    const isAuthenticated = !!currentAuthState;
    if (isAuthenticated && !checkAuthInterval) {
      // User just logged in
      startPeriodicCheck(reduxStore, signOutUserAction, api);
    } else if (!isAuthenticated && checkAuthInterval) {
      // User just logged out
      stopPeriodicCheck();
    }
  });

  // Initial check - if already authenticated, start periodic check
  const initialState = reduxStore.getState();
  if (initialState.authentication?.authentication) {
    startPeriodicCheck(reduxStore, signOutUserAction, api);
  }

  // Return cleanup function
  return () => {
    events.forEach((event) => {
      document.removeEventListener(event, checkAuthOnInteraction);
    });
    stopPeriodicCheck();
    if (storeSubscription) {
      storeSubscription();
      storeSubscription = null;
    }
  };
};

/**
 * Stop listening for authentication state changes
 */
export const stopAuthStateListener = (cleanupFn) => {
  if (cleanupFn && typeof cleanupFn === 'function') {
    cleanupFn();
  }
};
